import { useEffect, useState } from "react";
import { API } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { toast } from "sonner";
import { CheckCircle2, EyeOff, Eye, Star, Trash2, Pencil, Loader2, Quote } from "lucide-react";

const STATUS_FILTERS = [
  { key: "pending",   label: "Pending review" },
  { key: "published", label: "Published" },
  { key: "hidden",    label: "Hidden" },
];

const StoriesAdmin = () => {
  const [filter, setFilter] = useState("pending");
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/stories?status=${filter}`, { credentials: "include" });
      const data = await res.json();
      setStories(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error("Could not load stories");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [filter]);

  const patch = async (id, body, msg) => {
    try {
      const res = await fetch(`${API}/admin/stories/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      const text = await res.text();
      let data; try { data = JSON.parse(text); } catch { data = {}; }
      if (!res.ok) throw new Error(data?.detail || "Update failed");
      toast.success(msg || "Saved");
      await load();
    } catch (err) { toast.error(err.message); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this story permanently?")) return;
    try {
      const res = await fetch(`${API}/admin/stories/${id}`, { method: "DELETE", credentials: "include" });
      if (!res.ok) throw new Error();
      toast.success("Deleted");
      await load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="space-y-5" data-testid="stories-admin">
      <Card className="border border-slate-200">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="text-slate-900">Success Stories</CardTitle>
            <p className="text-xs text-slate-500 mt-1">Review participant submissions. Approve to publish on the public <span className="font-mono">/stories</span> page.</p>
          </div>
          <div className="flex gap-1">
            {STATUS_FILTERS.map((f) => (
              <Button key={f.key} size="sm" variant={filter === f.key ? "default" : "outline"}
                onClick={() => setFilter(f.key)} data-testid={`stories-filter-${f.key}`}
                className={filter === f.key ? "btn-primary" : ""}>
                {f.label}
              </Button>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" /></div>
          ) : stories.length === 0 ? (
            <p className="text-sm text-slate-500 italic py-6 text-center">No stories in this state.</p>
          ) : (
            <ul className="space-y-3">
              {stories.map((s) => (
                <li key={s.story_id} className="border border-slate-200 rounded-xl p-4" data-testid={`story-admin-${s.story_id}`}>
                  <div className="flex justify-between gap-3 mb-1 flex-wrap">
                    <div className="flex-1 min-w-[240px]">
                      <p className="font-heading font-semibold text-slate-900 flex items-center gap-2">
                        {s.featured && <Star className="w-4 h-4 text-[#FFD507] fill-[#FFD507]" />}
                        {s.title}
                      </p>
                      {s.outcome && <p className="text-xs text-emerald-700 font-semibold uppercase tracking-wider mt-0.5">{s.outcome}</p>}
                      <p className="text-xs text-slate-500 mt-1">
                        By <span className="font-medium">{s.anonymous ? "(anonymous)" : s.author_name}</span>{" "}
                        · {s.author_email} · submitted {new Date(s.submitted_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <p className="text-sm text-slate-700 mt-2 whitespace-pre-wrap line-clamp-5">{s.story}</p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {s.status !== "published" && (
                      <Button size="sm" className="btn-primary" onClick={() => patch(s.story_id, { status: "published" }, "Published")}
                        data-testid={`approve-story-${s.story_id}`}>
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve &amp; publish
                      </Button>
                    )}
                    {s.status !== "hidden" && (
                      <Button size="sm" variant="outline" onClick={() => patch(s.story_id, { status: "hidden" }, "Hidden")}
                        data-testid={`hide-story-${s.story_id}`}>
                        <EyeOff className="w-3.5 h-3.5 mr-1" /> Hide
                      </Button>
                    )}
                    {s.status === "hidden" && (
                      <Button size="sm" variant="outline" onClick={() => patch(s.story_id, { status: "published" }, "Re-published")}>
                        <Eye className="w-3.5 h-3.5 mr-1" /> Re-publish
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => patch(s.story_id, { featured: !s.featured }, s.featured ? "Un-featured" : "Featured")}
                      data-testid={`feature-story-${s.story_id}`}>
                      <Star className="w-3.5 h-3.5 mr-1" /> {s.featured ? "Un-feature" : "Feature"}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setEditing(s)} data-testid={`edit-story-${s.story_id}`}>
                      <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => remove(s.story_id)} data-testid={`delete-story-${s.story_id}`}>
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <EditStoryDialog story={editing} onClose={() => setEditing(null)} onSaved={load} />
    </div>
  );
};


const EditStoryDialog = ({ story, onClose, onSaved }) => {
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [outcome, setOutcome] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (story) {
      setTitle(story.title || "");
      setText(story.story || "");
      setOutcome(story.outcome || "");
    }
  }, [story]);

  if (!story) return null;
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/stories/${story.story_id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ title, story: text, outcome }),
      });
      if (!res.ok) throw new Error();
      toast.success("Saved");
      onSaved();
      onClose();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!story} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Quote className="w-4 h-4 text-[#1E3A8A]" /> Edit story</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <div><Label>Title</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} /></div>
          <div><Label>Outcome</Label><Input value={outcome} onChange={(e) => setOutcome(e.target.value)} /></div>
          <div><Label>Story</Label><Textarea rows={8} value={text} onChange={(e) => setText(e.target.value)} /></div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} className="btn-primary">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default StoriesAdmin;
